import React, {forwardRef} from 'react';
import {TableFunctions} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/Table.type';
import {BaselineItem, RoleRow, EyeOptionMap, Company} from './BaselineTypes';
import {useIntl} from 'react-intl';
import {Table} from '@forecast-it/design-system';
import Util from '../../forecast-app/shared/util/util';
import {estimateIsInDays, hoursToDays, daysToHours, OnSaveError, OnSaveErrorType} from './Util';

type RoleTableProps = {
	editMode?: boolean;
	onDeleteRow: (row: RoleRow) => void;
	onRoleSelect: (roleId: string, row: RoleRow) => void;
	company: Company;
	currency: string;
	ref: React.MutableRefObject<TableFunctions>;
	recalculate?: (row: RoleRow) => void;
	setTableData?: React.Dispatch<React.SetStateAction<BaselineItem[]>>;
	tableData: RoleRow[];
	eyeOptionMap?: EyeOptionMap;
	onSaveError?: OnSaveError;
	disabledRoleIds?: string[];
	minutesPerDay?: number | null;
};

export const TimeEstimatesTable = forwardRef<TableFunctions, RoleTableProps>((props, ref) => {
	const {
		editMode,
		onDeleteRow,
		company,
		currency,
		onRoleSelect,
		recalculate,
		tableData,
		setTableData,
		eyeOptionMap,
		onSaveError,
		disabledRoleIds,
		minutesPerDay,
	} = props;

	const {formatMessage, formatPlural} = useIntl();
	const seesCost = Util.seesBaselineCost();

	const inDays = estimateIsInDays(minutesPerDay);
	const hoursPerDay = inDays ? minutesPerDay / 60 : undefined;

	const estimateHeader = formatMessage({id: inDays ? 'deliverable.columns.days' : 'deliverable.columns.hours'});

	// The row model is hours. `days` is derived purely for the input column and
	// converted straight back on change, so nothing downstream sees days.
	const rows = React.useMemo(
		() => (inDays ? tableData.map(row => ({...row, days: hoursToDays(row.hours, minutesPerDay)})) : tableData),
		[tableData, inDays, minutesPerDay]
	);

	const onDaysChange = (row: RoleRow & {days: number}) => {
		const {days, ...rest} = row;
		recalculate?.({...rest, hours: daysToHours(days, minutesPerDay as number)});
	};

	// In days mode, `rows` (passed as the table's data) carries a derived `days`
	// field per row (see above). The design system's dropdown/action columns
	// hand back `row.original` verbatim, so without stripping it here `days`
	// would leak into state via these callbacks even though it's never persisted.
	const stripDays = (row: RoleRow & {days?: number}): RoleRow => {
		const withoutDays = {...row};
		delete withoutDays.days;
		return withoutDays;
	};

	const onRoleSelectInDays = (roleId: string, row: RoleRow & {days?: number}) => onRoleSelect(roleId, stripDays(row));

	const onDeleteRowInDays = (row: RoleRow & {days?: number}) => onDeleteRow(stripDays(row));

	// The plural category comes from the locale via CLDR plural rules
	// (formatPlural, i.e. `new Intl.PluralRules(locale).select(...)`), not
	// from a hand-rolled numeric range: only English's "one" rule happens
	// to be "exactly 1". French's "one" also covers 0; Danish's "one" covers
	// 0.5 but not integer 0. `row.days` is already the rounded, displayed
	// value (see hoursToDays), so the word never disagrees with the number.
	const daysSuffix = (row: {days: number}) => {
		const isSingular = formatPlural(row.days) === 'one';
		return ` ${formatMessage({id: isSingular ? 'overview_time.day' : 'common.days'}).toLowerCase()}`;
	};

	const getRoleName = (row: RoleRow) => {
		const role = company?.roles?.edges?.find(edge => edge?.node?.id === row.roleId);
		if (role && role.node && role.node.name) {
			return role.node.name;
		}
		return formatMessage({id: 'deliverable.select_role'});
	};

	const sortedRoles = React.useMemo(() => {
		const edges = company?.roles?.edges || [];
		const eligibleEdges = disabledRoleIds?.length
			? edges.filter(edge => !edge?.node?.id || !disabledRoleIds.includes(edge.node.id))
			: edges;
		return [...eligibleEdges].sort((a, b) => Util.sortAlphabetically(a?.node?.name, b?.node?.name));
	}, [company?.roles?.edges, disabledRoleIds]);

	const getDropdownState = (row: RoleRow) => {
		const highlightAsInputError = onSaveError && onSaveError.type === OnSaveErrorType.MISSING_ROLE && !row.roleId;
		return highlightAsInputError ? 'error' : 'default';
	};

	return (
		<>
			<Table width={'100%'} tableRef={ref} setTableData={setTableData} data={rows}>
				{editMode ? (
					<Table.DropdownColumn
						header={formatMessage({id: 'deliverable.columns.role'})}
						label={getRoleName}
						state={getDropdownState}
						width="20%"
					>
						{sortedRoles.map(role => (
							<Table.DropdownColumn.ListItem
								onClick={row => {
									if (!role?.node?.id) return;
									inDays ? onRoleSelectInDays(role.node.id, row) : onRoleSelect(role.node.id, row);
								}}
							>
								{role?.node?.name}
							</Table.DropdownColumn.ListItem>
						))}
					</Table.DropdownColumn>
				) : (
					<Table.TextColumn
						accessorKey={'roleName'}
						header={formatMessage({id: 'deliverable.columns.role'})}
						ellipsis
					/>
				)}
				{editMode && inDays ? (
					<Table.NumberInputColumn
						accessorKey={'days'}
						header={estimateHeader}
						onInputChange={onDaysChange}
						suffixText={daysSuffix}
						width="20%"
					/>
				) : editMode ? (
					<Table.NumberInputColumn
						accessorKey={'hours'}
						header={estimateHeader}
						onInputChange={recalculate}
						width="20%"
					/>
				) : (
					<Table.TimeColumn
						accessorKey={'hours'}
						header={estimateHeader}
						align={Table.Align.right}
						timeUnit={inDays ? 'days' : 'hours'}
						hoursPerDay={hoursPerDay}
						visible={eyeOptionMap ? eyeOptionMap['time_estimates.hours'] : true}
					/>
				)}
				<Table.CurrencyColumn
					accessorKey={'valueOfService'}
					header={formatMessage({id: 'deliverable.columns.value_of_service'})}
					align={Table.Align.right}
					currency={currency}
					width="12%"
					visible={eyeOptionMap ? eyeOptionMap['time_estimates.value_of_service'] : true}
				/>
				<Table.CurrencyColumn
					accessorKey={'revenue'}
					header={formatMessage({id: 'deliverable.columns.revenue'})}
					align={Table.Align.right}
					currency={currency}
					width="12%"
					visible={eyeOptionMap ? eyeOptionMap['time_estimates.revenue'] : true}
				/>
				<Table.CurrencyColumn
					accessorKey={'cost'}
					header={formatMessage({id: 'deliverable.columns.cost'})}
					align={Table.Align.right}
					currency={currency}
					width="12%"
					visible={seesCost && (eyeOptionMap ? eyeOptionMap['time_estimates.cost'] : true)}
				/>
				<Table.CurrencyColumn
					accessorKey={'profit'}
					header={formatMessage({id: 'deliverable.columns.profit'})}
					align={Table.Align.right}
					currency={currency}
					width="12%"
					visible={seesCost && (eyeOptionMap ? eyeOptionMap['time_estimates.profit'] : true)}
				/>
				<Table.CurrencyColumn
					accessorKey={'ratePerHour'}
					header={formatMessage({id: 'deliverable.columns.rate_per_hour'})}
					align={Table.Align.right}
					currency={currency}
					width="12%"
					visible={eyeOptionMap ? eyeOptionMap['time_estimates.rate_per_hour'] : true}
				/>
				<Table.CurrencyColumn
					accessorKey={'costPerHour'}
					header={formatMessage({id: 'deliverable.columns.cost_per_hour'})}
					align={Table.Align.right}
					currency={currency}
					width="12%"
					visible={seesCost && (eyeOptionMap ? eyeOptionMap['time_estimates.cost_per_hour'] : true)}
				/>
				<Table.ActionColumn header={'contextmenu'} hideHeader hidden={!editMode}>
					<Table.ActionColumn.Option
						data-cy={'delete-baseline-role'}
						onClick={inDays ? onDeleteRowInDays : onDeleteRow}
					>
						{formatMessage({id: 'deliverable.columns.delete_time_estimate'})}
					</Table.ActionColumn.Option>
				</Table.ActionColumn>
			</Table>
		</>
	);
});
